import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { RESET_PASSWORD } from '../graphql';
import { Eye, EyeOff, CheckCircle, ArrowRight } from 'lucide-react';

export default function ResetPasswordPage() {
  const [params]                    = useSearchParams();
  const token                       = params.get('token') || '';
  const [password, setPassword]     = useState('');
  const [confirm, setConfirm]       = useState('');
  const [showPass, setShowPass]     = useState(false);
  const [done, setDone]             = useState(false);
  const [error, setError]           = useState('');
  const navigate                    = useNavigate();
  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

  useEffect(() => {
    if (!token) setError('Invalid or missing reset link. Please request a new one.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    try {
      const { data, errors } = await resetPassword({ variables: { token, newPassword: password } });
      if (errors?.length) throw new Error(errors[0].message);
      if (!data?.resetPassword) throw new Error('Reset failed. The link may have expired.');
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('status code') || msg.includes('fetch')) {
        setError('Cannot reach server. Please try again.');
      } else {
        setError(msg || 'Something went wrong.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-surface-900 px-6">
      <div className="w-full max-w-[360px]">

        {done ? (
          /* ── Success ── */
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mx-auto mb-5 bg-green-600">
              <CheckCircle size={26} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Password updated</h2>
            <p className="text-gray-400 dark:text-white/40 text-sm">
              You're being redirected to sign in…
            </p>
            <Link to="/login"
              className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-brand-600 dark:text-brand-400 hover:underline">
              Sign in now <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          /* ── Form ── */
          <>
            <div className="mb-8">
              <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-widest mb-2">
                New password
              </p>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Set a new password</h2>
              <p className="text-gray-400 dark:text-white/40 mt-1.5 text-sm">
                Must be at least 8 characters.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <input
                    className="input pr-11"
                    type={showPass ? 'text' : 'password'}
                    placeholder="8+ characters"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 dark:text-white/25 hover:text-gray-500 dark:hover:text-white/50 transition-colors">
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide mb-1.5">
                  Confirm password
                </label>
                <input
                  className="input"
                  type="password"
                  placeholder="Repeat password"
                  required
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                  <div className="w-4 h-4 rounded-full bg-red-100 dark:bg-red-800/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-500 text-[10px] font-black">!</span>
                  </div>
                  <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token}
                className="w-full h-11 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 transition-colors active:scale-[0.98] disabled:opacity-50"
              >
                {loading
                  ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><span>Update password</span><ArrowRight size={15} /></>}
              </button>
            </form>

            <p className="text-center text-sm text-gray-400 dark:text-white/30 mt-5">
              <Link to="/forgot-password" className="text-brand-600 dark:text-brand-400 hover:underline font-medium">
                Request a new link
              </Link>
              {' · '}
              <Link to="/login" className="hover:text-gray-600 dark:hover:text-white/60 transition-colors">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
